import { memo, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KentuckyKYC, SeonPlugin } from "@leapfinancial/kentucky-sdk";
import "@leapfinancial/kentucky-sdk/styles";
import { config } from "../config";
import { APP_ROUTES } from "../constants";
import { useSessionStore } from "../store/useSessionStore";
import { useStore } from "../store/globalState";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { safeCapture } from "../utils/posthog";
import { getRouteAfterCipGate } from "../utils/phoneNumberFlow";

interface CipPageLocationState {
  cipTicketId: string;
}

export const CipPage = memo(() => {
  useClaimLink()
  const navigate = useNavigate();
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const { t } = useTranslation();
  const { session, updateSession } = useSessionStore();

  const state = location.state as CipPageLocationState | null;
  const cipTicketId = state?.cipTicketId;

  // Theme comes from the link claim data stored in session
  const partnerTheme = session.data?.partner?.theme;

  // Build plugin list
  const plugins = useMemo(() => [SeonPlugin()], []);

  useEffect(() => {
    if (cipTicketId) {
      safeCapture("ux.cip.identity_review.screen.view", { cip_ticket_id: cipTicketId });
    }
  }, [cipTicketId]);

  // If no cipTicketId, redirect to error page
  if (!cipTicketId) {
    navigate(APP_ROUTES.ERROR_PAGE, {
      state: {
        title: t("error.title"),
        subTitle: null,
        message: t("error.tryAgainLater"),
        btnText: t("common.retry"),
        isOut: false,
        pageUrl: APP_ROUTES.TRANSFER_PAGE,
      },
    });
    return null;
  }

  return (
    <KentuckyKYC
      ticketId={cipTicketId}
      apiBaseUrl={config.KENTUCKY_API_BASE_URL}
      googleMapsApiKey={config.GOOGLE_MAP_API_KEY}
      awsRegion={config.AWS_REGION || "us-east-1"}
      theme={partnerTheme}
      plugins={plugins}
      onComplete={(result) => {
        console.log("[CipPage] KYC flow completed:", result);

        // The SDK also reports here when its final `completeTicket` call
        // failed (`success: false`). The ticket is not closed in that case,
        // so treat it like an error instead of moving on.
        if (!result.success) {
          safeCapture("ux.cip.complete.error", {
            cip_ticket_id: result.ticketId,
          });
          navigate(APP_ROUTES.ERROR_PAGE, {
            state: {
              title: t("error.title"),
              subTitle: null,
              message: t("error.tryAgainLater"),
              btnText: t("common.retry"),
              isOut: false,
              pageUrl: APP_ROUTES.TRANSFER_PAGE,
            },
          });
          return;
        }

        // Remember in-session that CIP ran. The backend's cipStatus flips
        // only when Kentucky's webhook lands, so without this the next
        // transfer would be sent through CIP again (bug: duplicate CIP).
        const current = useSessionStore.getState().session.data;
        if (current) {
          updateSession({
            ...current,
            cipCompletedAt: new Date().toISOString(),
          });
        }

        redirectTo(
          getRouteAfterCipGate(
            current?.hasPhoneNumber,
            useStore.getState().phonePromptSkipped
          ),
          { replace: true }
        );
      }}
      onError={(error) => {
        console.error("[CipPage] KYC flow error:", error);
        navigate(APP_ROUTES.ERROR_PAGE, {
          state: {
            title: t("error.title"),
            subTitle: null,
            message:
              error instanceof Error ? error.message : t("error.tryAgainLater"),
            btnText: t("common.retry"),
            isOut: false,
            pageUrl: APP_ROUTES.TRANSFER_PAGE,
          },
        });
      }}
    />
  );
});
